import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  MenuItem,
  TextField,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { bool, func } from "prop-types";
import cogoToast from "cogo-toast";
import {
  getEmployerCorporateFieldErrors,
  isSocialProfileHost,
  parseWebsiteHost,
} from "./employerVerificationValidation";

const HIRING_PURPOSE_OPTIONS = [
  { value: "direct_hire", label: "Direct Hire" },
  { value: "recruitment_agency", label: "Recruitment Agency" },
  { value: "staffing", label: "Staffing" },
  { value: "consulting", label: "Consulting" },
  { value: "other", label: "Other" },
];

const EmployerVerificationModal = ({ open, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    company_name: "",
    company_email: "",
    website_url: "",
    hiring_purpose: "direct_hire",
    contact_person: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const corporateErrors = useMemo(
    () =>
      getEmployerCorporateFieldErrors({
        company_email: form.company_email,
        website_url: form.website_url,
      }),
    [form.company_email, form.website_url]
  );

  const parsedWebsiteHost = useMemo(
    () => parseWebsiteHost(form.website_url),
    [form.website_url]
  );

  const websiteHelperText =
    corporateErrors.website_url ||
    (parsedWebsiteHost && isSocialProfileHost(parsedWebsiteHost)
      ? "If you add your company homepage (not only a social profile), your work email must use that domain (e.g. name@company.com)."
      : "");

  const baseFieldsValid =
    form.company_name.trim() &&
    form.company_email.trim() &&
    form.hiring_purpose &&
    form.contact_person.trim();

  const corporateValid =
    !corporateErrors.company_email && !corporateErrors.website_url;

  const isValid = baseFieldsValid && corporateValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!baseFieldsValid || submitting || !onSubmit) return;

    if (!corporateValid) {
      const first =
        corporateErrors.company_email ||
        corporateErrors.website_url ||
        "Please fix the highlighted fields.";
      cogoToast.error(first, { position: "top-center" });
      return;
    }

    setSubmitting(true);
    try {
      const success = await onSubmit(form);
      if (success) {
        onClose();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit verification. Please try again.";
      cogoToast.error(msg, { position: "top-center" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      aria-labelledby="employer-verification-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: "1rem", padding: "0.5rem" },
      }}
    >
      <div className="d-flex justify-content-between align-items-center px-2">
        <DialogTitle id="employer-verification-title" style={{ padding: "16px 0" }}>
          Verify your employer account to publish this job
        </DialogTitle>
        <IconButton onClick={onClose} disabled={submitting} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent>
        <p
          style={{
            margin: "0 0 20px",
            color: "#64748b",
            fontSize: "0.9rem",
            lineHeight: 1.5,
          }}
        >
          Verification protects candidates and maintains hiring quality. This
          usually takes just a few minutes. Once your email is confirmed, your
          job is published automatically and you can manage it from My Jobs.
        </p>

        <form id="employer-verification-form" onSubmit={handleSubmit}>
          <TextField
            label="Company Name"
            name="company_name"
            value={form.company_name}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            variant="outlined"
            size="small"
            disabled={submitting}
          />
          <TextField
            label="Company Email (Work Email)"
            name="company_email"
            type="email"
            value={form.company_email}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            variant="outlined"
            size="small"
            disabled={submitting}
            error={Boolean(corporateErrors.company_email)}
            helperText={corporateErrors.company_email || undefined}
          />
          <TextField
            label="Company Website or LinkedIn URL"
            name="website_url"
            value={form.website_url}
            onChange={handleChange}
            fullWidth
            margin="normal"
            variant="outlined"
            size="small"
            placeholder="https://yourcompany.com or LinkedIn URL"
            disabled={submitting}
            error={Boolean(corporateErrors.website_url)}
            helperText={websiteHelperText || undefined}
          />
          <TextField
            select
            label="Hiring Purpose"
            name="hiring_purpose"
            value={form.hiring_purpose}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            variant="outlined"
            size="small"
            disabled={submitting}
          >
            {HIRING_PURPOSE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Contact Person Name"
            name="contact_person"
            value={form.contact_person}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            variant="outlined"
            size="small"
            disabled={submitting}
          />
        </form>
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px 20px" }}>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          style={{
            padding: "10px 20px",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            backgroundColor: "#fff",
            cursor: "pointer",
            fontSize: "0.9rem",
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="employer-verification-form"
          disabled={!isValid || submitting}
          style={{
            padding: "10px 24px",
            border: "none",
            borderRadius: "8px",
            backgroundColor: isValid && !submitting ? "#1d4ed8" : "#94a3b8",
            color: "#fff",
            cursor: isValid && !submitting ? "pointer" : "not-allowed",
            fontSize: "0.9rem",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {submitting && (
            <CircularProgress size={14} style={{ color: "#fff" }} />
          )}
          {submitting ? "Submitting..." : "Submit for Verification"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

EmployerVerificationModal.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  onSubmit: func,
};

export default EmployerVerificationModal;
